// 分割条
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IConnectorModel } from '@src/slide/domain/ui-model/element-modeles/Interface/connector-model.interface';
import { canvasToPt } from '@src/slide/infrastructure/module/independent/unit';
import { VisualBlock, VisualBlockType } from './visual-block';
import { LineType } from '@src/slide/infrastructure/module/dependent/line-info';
import { IRect } from '@src/slide/infrastructure/module/independent/graphic/rect/rect';
import { IElementNode } from '@src/slide/application/service/slide-content-enumerator/enumerator-types.interface';

export enum SeparatorType {
    Horizontal = 'horizontal', // 水平分隔条
    Vertical = 'vertical', // 竖直分隔条
}


export class SeparatorRect {
    public rect: IRect;
    public type: SeparatorType;
    public separatorBlock1: VisualBlock;
    public separatorBlock2: VisualBlock;
    /**
     * 分隔条一侧和相邻分隔条之间的页面块
     */
    public blocks1: Array<VisualBlock> = [];
    /**
     * 分隔条另一侧和相邻分隔条之间的页面块
     */
    public blocks2: Array<VisualBlock> = [];
    /**
     * 分隔条一侧的所有页面块
     */
    public allBlocks1: Array<VisualBlock> = [];
    /**
     * 分隔条另一侧的所有页面块
     */
    public allBlocks2: Array<VisualBlock> = [];
    private totalRect: IRect;

    private _weight?: number;

    public constructor(type: SeparatorType, rect: IRect, totalRect: IRect) {
        this.type = type;
        this.totalRect = totalRect;
        if (type === SeparatorType.Horizontal) {
            this.separatorBlock1 = new VisualBlock({ left: rect.left, top: rect.top, width: rect.width, height: 0 });
            this.separatorBlock2 = new VisualBlock({
                left: rect.left, top: rect.top + rect.height,
                width: rect.width, height: 0,
            });
        } else {
            this.separatorBlock1 = new VisualBlock({
                left: rect.left, top: rect.top, width: 0, height: rect.height });
            this.separatorBlock2 = new VisualBlock({
                left: rect.left + rect.width, top: rect.top,
                width: 0, height: rect.height,
            });
        }
        this.rect = rect;
    }

    // 权重，权重越大表示分割条两侧的页面属于不同的语义块的可能性就越大。
    // 距离越远，权重越大
    // 如果是通过标签获取的，则权重越大？？？？？？
    // 如果两侧的页面块的背景是不同的，则权重越大
    // 如果两侧的页面块的字体属性，比如字体大小，重量是不同的，则权重将增加。
    // 如果两侧的结构非常相似，比如文本，则权重降低。
    public getWeight(): number {
        if (this._weight !== undefined) return this._weight;
        this._weight = 10;
        const distanceValue = this.distanceValue();
        const textValueDiff = this.textSizeValue();
        this._weight += textValueDiff;
        const connectorValue = this.connectorValue();
        this._weight += connectorValue;
        const hasTextDiff = this.hasTextValue();
        this._weight += hasTextDiff;
        return this._weight;
    }

    public distance(): number {
        if (this.type === SeparatorType.Horizontal) {
            return this.rect.height;
        }
        return this.rect.width;
    }

    private distanceValue(): number {
        if (this.type === SeparatorType.Horizontal) {
            return this.rect.height / this.totalRect.height;
        }
        return this.rect.width / this.totalRect.width;
    }

    /**
     * 文本权重
     * @private
     */
    private textSizeValue(): number {
        let maxBlock1: VisualBlock | undefined;
        let maxBlock2: VisualBlock | undefined;
        let maxSize1 = 0;
        let maxSize2 = 0;
        for (const visualBlock of this.blocks1) {
            if (visualBlock.getFirstFontSize() > maxSize1) {
                maxBlock1 = visualBlock;
                maxSize1 = visualBlock.getFirstFontSize();
            }
        }
        for (const visualBlock of this.blocks2) {
            if (visualBlock.getFirstFontSize() > maxSize2) {
                maxBlock2 = visualBlock;
                maxSize2 = visualBlock.getFirstFontSize();
            }
        }
        const sizeDiff = Math.abs(maxSize1 - maxSize2);
        // 有一边没有文字，没有对比的意义
        if (maxSize1 === 0 || maxSize2 === 0) return 1;
        let result = sizeDiff / 200 * 10;
        if (maxSize2 - maxSize1 > 10) result += 5;
        return result;
    }

    /**
     * 分隔条两边的内容类型的区别权重
     * @private
     */
    private hasTextValue(): number {
        let hasText1 = false;
        let hasText2 = false;
        for (const visualBlock of this.allBlocks1) {
            if (visualBlock.hasText()) {
                hasText1 = true;
                break;
            }
        }
        for (const visualBlock of this.allBlocks2) {
            if (visualBlock.hasText()) {
                hasText2 = true;
                break;
            }
        }
        if ((hasText1 && !hasText2)
            || (!hasText1 && hasText2)) {
            return 10;
        }
        return 0;
    }

    /**
     * 连接线差异值，如果一边是连接线，另一边是元素，则认为连接线是分隔符，提升连接线的权重
     * @private
     */
    // eslint-disable-next-line complexity
    private connectorValue(): number {
        if (this.separatorBlock1.blockType() === VisualBlockType.Element) {
            const elementNode = this.separatorBlock1.info as IElementNode;
            if (elementNode.element.type === UIModelType.CONNECTOR) {
                const connectorModel = (elementNode.element as IConnectorModel);
                const frameOnSlide = connectorModel.getFrame().frameOnSlide();
                if (((this.type === SeparatorType.Horizontal
                        && frameOnSlide.height <= 0
                        && frameOnSlide.width / this.rect.width > 2 / 3))
                    || (this.type === SeparatorType.Vertical
                        && frameOnSlide.width <= 0
                        && frameOnSlide.height / this.rect.height > 2 / 3)
                ) {
                    const lineType = connectorModel.getGraphic().getGraphicLinInfo()?.bgLine;
                    if (lineType === LineType.LINE || lineType === LineType.STRAIGNTCONNECTOR1) {
                        if (canvasToPt(Number(connectorModel.getBorder().getBorderInfo()?.width)) < 20) {
                            // 水平或者垂直连接线忽略
                            return 10;
                        }
                    }
                }
            }
        }
        return 0;
    }

    // /**
    //  * 内容相似度
    //  * @private
    //  */
    // private similarValue(): number {
    //
    // }
}
